import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Send,
  Loader2,
  MessageCircle,
  Package,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  DollarSign,
  RefreshCw,
  Download,
  AlertTriangle,
  User,
  Shield,
} from 'lucide-react';
import { api } from '../../api/client';
import { Can, useCan } from '../../lib/rbac';
import { MessageTemplates } from './MessageTemplates';
import {
  ASSISTANT_WATCH_HELP,
  ASSISTANT_WATCH_LABEL,
  ASSISTANT_WATCH_NOTE_ID,
  ASSISTANT_WATCH_NOTE_MAX,
  ASSISTANT_WATCH_NOTE_PLACEHOLDER,
  ASSISTANT_WATCH_TOGGLE_ID,
  repliedBadge,
  isForbidden,
  showAssistantWatch,
  validateWatchNote,
  watchErrorText,
  type AssistantWatch,
} from './assistantWatchView';

/* ── Types (mirrors backend ConversationDetail) ─────────── */

interface MessagePanelProps {
  packId: number;
  buyerName: string;
  sellerId: number;
  onClose: () => void;
  /**
   * `false` (por defecto): cajón a pantalla completa sobre el fondo oscuro, como en Órdenes.
   * `true`: el MISMO chat metido dentro de otra tarjeta (pop-up del depósito) — sin fondo oscuro,
   * sin taparlo todo y con alto acotado. Solo cambia el envoltorio; el contenido es idéntico.
   */
  embedded?: boolean;
  /**
   * De dónde se leen y se mandan los mensajes. `{packId}` se reemplaza por el número de paquete.
   *
   * Por defecto, las rutas de la pantalla de Mensajes (piden `messages.view` / `messages.send`).
   * El depósito pasa las SUYAS (`/warehouse/packs/{packId}/messages`), que piden la misma llave
   * que el escaneo: así el operario lee y contesta desde el pop-up de cierre sin que haya que
   * darle los permisos de la pantalla de Mensajes.
   */
  basePath?: string;
  /**
   * Llave que habilita el cuadro para responderle al comprador. Por defecto la de la pantalla de
   * Mensajes; el pop-up del depósito pasa la SUYA (`warehouse.operate`), la misma que ya le pide
   * la ruta de envío del depósito. Sin esto el operario veía la conversación y no podía contestar.
   */
  sendCap?: string;
  /**
   * Interruptor "Seguimiento Asistente". Por defecto se muestra solo con las rutas de Órdenes
   * (`MESSAGE_PANEL_DEFAULT_BASE`); el pop-up del depósito usa otras y no lo ve.
   */
  allowAssistantWatch?: boolean;
}

/** Rutas de la pantalla de Mensajes: leer, responder, marcar leído y mandar la factura. */
export const MESSAGE_PANEL_DEFAULT_BASE = '/mercadolibre/messages/conversations/{packId}';

/* ── Collapsible Section ─────────────────────────────────── */

function CollapsibleSection({
  title, icon, expanded, onToggle, children,
}: {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-[var(--color-border)] last:border-b-0">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {icon}
        {title}
      </button>
      {expanded && <div className="px-3 pb-2">{children}</div>}
    </div>
  );
}

/* ── Component ───────────────────────────────────────────── */

export function MessagePanel({
  packId, buyerName, sellerId, onClose,
  embedded = false,
  basePath = MESSAGE_PANEL_DEFAULT_BASE,
  sendCap = 'orders.messages',
  allowAssistantWatch,
}: MessagePanelProps) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const can = useCan();
  const [sending, setSending] = useState(false);
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  // Datos de la venta que el texto predeterminado pegado no pudo completar (avisa debajo del cuadro).
  const [templateMissing, setTemplateMissing] = useState<string[]>([]);
  // Metido en el pop-up del depósito los datos de la venta ya están arriba: arranca con el
  // contexto plegado para que se vean los mensajes y no una repetición.
  const [expandedSection, setExpandedSection] = useState<string | null>(embedded ? null : 'sale');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Camino base ya resuelto (con el número de paquete puesto) + si estamos en el juego REDUCIDO
  // de rutas: las del depósito solo saben leer y responder. Marcar leído y mandar la factura son
  // de la pantalla de Mensajes, así que ahí ni se intentan (no existen, darían 404).
  const resource = basePath.replace('{packId}', String(packId));
  const soloLeerYResponder = basePath !== MESSAGE_PANEL_DEFAULT_BASE;

  // "Seguimiento Asistente": solo desde Órdenes y con la llave de mensajes de la venta.
  const watchVisible =
    showAssistantWatch(basePath, MESSAGE_PANEL_DEFAULT_BASE, allowAssistantWatch) &&
    can('orders.messages');
  const [watch, setWatch] = useState<AssistantWatch | null>(null);
  const [watchNote, setWatchNote] = useState('');
  const [watchSaving, setWatchSaving] = useState(false);
  const [watchError, setWatchError] = useState<string | null>(null);
  const watchUrl = `${MESSAGE_PANEL_DEFAULT_BASE.replace('{packId}', String(packId))}/assistant-watch`;

  useEffect(() => {
    if (!watchVisible) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`${watchUrl}?seller_id=${sellerId}`) as any;
        if (!alive) return;
        if (res?.success && res.data) {
          setWatch(res.data);
          setWatchNote(res.data.note || '');
        } else if (isForbidden(res)) {
          setWatchError(watchErrorText(res));
        }
      } catch {
        /* sin el estado, el interruptor arranca apagado */
      }
    })();
    return () => { alive = false; };
  }, [watchVisible, watchUrl, sellerId]);

  const saveWatch = async (enabled: boolean, noteText?: string) => {
    if (watchSaving) return;
    const body: Record<string, unknown> = { seller_id: sellerId, enabled };
    if (noteText !== undefined) {
      const v = validateWatchNote(noteText);
      if (!v.ok) { setWatchError(v.error); return; }
      body.note = v.value;
    }
    setWatchSaving(true);
    setWatchError(null);
    try {
      const res = await api.put(watchUrl, body) as any;
      if (res?.success && res.data) {
        setWatch(res.data);
        setWatchNote(res.data.note || '');
      } else {
        setWatchError(watchErrorText(res));
      }
    } catch {
      setWatchError(watchErrorText(null));
    } finally {
      setWatchSaving(false);
    }
  };

  const watchNoteDirty = (watchNote.trim() || null) !== (watch?.note || null);
  const watchBadge = repliedBadge(watch);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`${resource}?seller_id=${sellerId}`) as any;
      if (res.success && res.data) {
        setDetail(res.data);
      } else {
        setError(res.error || 'Failed to load messages');
      }
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [resource, sellerId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const msgs = detail?.messages?.messages || [];

  useEffect(() => {
    if (msgs.length > 0) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [msgs.length]);

  // Mark as read on open — solo donde esa ruta existe (pantalla de Mensajes)
  useEffect(() => {
    if (soloLeerYResponder) return;
    api.post(`${resource}/mark-read?seller_id=${sellerId}`, {}).catch(() => {});
  }, [resource, sellerId, soloLeerYResponder]);

  const handleSendMessage = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(
        `${resource}/send?seller_id=${sellerId}`,
        { text: message.trim() }
      ) as any;
      if (res.success) {
        setMessage('');
        await fetchDetail();
      } else {
        alert(`Error: ${res.error || 'Failed to send'}`);
      }
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  /** Pega el texto predeterminado ya completado: si el cuadro tiene algo, va debajo. */
  const handleInsertTemplate = (text: string, missing: string[]) => {
    setMessage((prev) => (prev.trim() ? `${prev.trimEnd()}\n${text}` : text));
    setTemplateMissing(missing);
  };

  const handleSendInvoice = async () => {
    if (invoiceSending || invoiceStatus === 'sent') return;
    setInvoiceSending(true);
    try {
      const res = await api.post(`${resource}/send-invoice?seller_id=${sellerId}`, {}) as any;
      if (res.success) {
        setInvoiceStatus('sent');
        await fetchDetail();
      } else {
        setInvoiceStatus('error');
        alert('Error enviando invoice: ' + (res.error || 'Unknown'));
      }
    } catch {
      setInvoiceStatus('error');
    } finally {
      setInvoiceSending(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const buyerId = detail?.order?.buyer_id;
  const siteId = detail?.order?.context_site?.toUpperCase() || 'MLA';
  const conversationActive = detail?.messages?.conversation_status?.status === 'active';
  const conversationBlocked = detail?.messages?.conversation_status?.status === 'blocked';

  return (
    <div
      className={embedded ? '' : 'fixed inset-0 bg-black/50 z-50 flex justify-end'}
      onClick={embedded ? undefined : onClose}
    >
      <div
        className={
          embedded
            ? 'w-full h-[460px] bg-[var(--color-bg)] flex flex-col rounded-xl border border-[var(--color-border)] overflow-hidden'
            : 'w-full max-w-lg bg-[var(--color-bg)] h-full flex flex-col shadow-xl'
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle size={18} className="text-blue-500 shrink-0" />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-[var(--color-text)]">
                {detail?.order?.buyer_nickname || buyerName}
              </h3>
              <div className="text-xs text-[var(--color-text-muted)] flex items-center gap-2">
                <span>#{detail?.order?.ml_order_id || packId}</span>
                {detail?.order?.context_site && <span>{detail.order.context_site}</span>}
                {detail?.order?.paid_amount && can('global.view_money') && <span>${Number(detail.order.paid_amount).toFixed(2)} {detail.order.currency_id}</span>}
                {detail?.order?.status && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                    detail.order.status === 'paid' ? 'bg-green-500/10 text-green-500' :
                    detail.order.status === 'cancelled' ? 'bg-red-500/10 text-red-500' :
                    'bg-yellow-500/10 text-yellow-500'
                  }`}>
                    {detail.order.status}
                  </span>
                )}
                {detail?.claim && (
                  <span className="px-1.5 py-0.5 bg-red-500/10 text-red-500 text-[10px] rounded-full flex items-center gap-0.5">
                    <AlertTriangle className="w-2.5 h-2.5" /> {detail.claim.type}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {watchVisible && (
              <div className="flex items-center gap-1.5" title={ASSISTANT_WATCH_HELP}>
                {watchBadge && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/10 text-amber-600">
                    {watchBadge}
                  </span>
                )}
                <input
                  id={ASSISTANT_WATCH_TOGGLE_ID}
                  type="checkbox"
                  role="switch"
                  aria-checked={!!watch?.enabled}
                  checked={!!watch?.enabled}
                  disabled={watchSaving}
                  onChange={(e) => saveWatch(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[var(--color-primary)] cursor-pointer"
                />
                <label
                  htmlFor={ASSISTANT_WATCH_TOGGLE_ID}
                  className="text-xs text-[var(--color-text-muted)] cursor-pointer select-none whitespace-nowrap"
                >
                  {ASSISTANT_WATCH_LABEL}
                </label>
              </div>
            )}
            <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--color-surface-hover)] shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>
        {watchVisible && (watch?.enabled || watchError) && (
          <div className="px-4 py-2 border-b border-[var(--color-border)] text-xs">
            {watch?.enabled && (
              <div className="flex items-center gap-2">
                <label htmlFor={ASSISTANT_WATCH_NOTE_ID} className="sr-only">
                  {ASSISTANT_WATCH_NOTE_PLACEHOLDER}
                </label>
                <input
                  id={ASSISTANT_WATCH_NOTE_ID}
                  type="text"
                  value={watchNote}
                  maxLength={ASSISTANT_WATCH_NOTE_MAX}
                  placeholder={ASSISTANT_WATCH_NOTE_PLACEHOLDER}
                  onChange={(e) => setWatchNote(e.target.value)}
                  onBlur={() => { if (watchNoteDirty) saveWatch(true, watchNote); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (watchNoteDirty) saveWatch(true, watchNote); } }}
                  className="flex-1 min-w-0 px-2 py-1 bg-[var(--color-surface)] border border-[var(--color-border)] rounded text-xs text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
                />
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => saveWatch(true, watchNote)}
                  disabled={watchSaving || !watchNoteDirty}
                  className="px-2 py-1 rounded border border-[var(--color-border)] text-xs hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
                >
                  {watchSaving ? '...' : 'Guardar'}
                </button>
              </div>
            )}
            {watchError && <div className="mt-1 text-[var(--color-error)]">{watchError}</div>}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex-1 flex justify-center items-center">
            <Loader2 size={24} className="animate-spin text-[var(--color-text-muted)]" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex-1 flex flex-col justify-center items-center text-sm text-[var(--color-error)]">
            {error}
            <button onClick={fetchDetail} className="mt-2 text-[var(--color-primary)] underline">Reintentar</button>
          </div>
        )}

        {!loading && !error && detail && (
          <>
            {/* Context panel (collapsible) */}
            <div className="border-b border-[var(--color-border)] max-h-[280px] overflow-y-auto">
              {/* Sale section */}
              <CollapsibleSection
                title="Venta ML"
                icon={<Package className="w-3.5 h-3.5" />}
                expanded={expandedSection === 'sale'}
                onToggle={() => toggleSection('sale')}
              >
                <div className="space-y-1.5 text-xs">
                  {detail.order?.items?.map((item: any, i: number) => (
                    <div key={i} className="flex items-center gap-2">
                      {item.thumbnail && <img src={item.thumbnail} className="w-8 h-8 rounded border border-[var(--color-border)]" />}
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-[var(--color-text)]">{item.title || item.item_id}</div>
                        <div className="text-[var(--color-text-muted)]">
                          {item.quantity}x{can('global.view_money') ? ` $${Number(item.unit_price).toFixed(2)} · Fee: $${Number(item.sale_fee || 0).toFixed(2)}` : ''} · SKU: {item.seller_sku}
                        </div>
                      </div>
                    </div>
                  ))}
                  {detail.shipment && (
                    <div className="text-[var(--color-text-muted)]">
                      Envio: <span className="text-[var(--color-text)]">{detail.shipment.status}</span>
                      {detail.shipment.tracking_number && ` · ${detail.shipment.tracking_number}`}
                    </div>
                  )}
                  {detail.profit !== null && detail.profit !== undefined && can('global.view_money') && (
                    <div className="text-[var(--color-text-muted)]">
                      Profit: <span className={detail.profit >= 0 ? 'text-green-500' : 'text-red-500'}>
                        ${detail.profit.toFixed(2)} ({detail.margin_pct}%)
                      </span>
                    </div>
                  )}
                </div>
              </CollapsibleSection>

              {/* Purchase section */}
              {(detail.amazon_purchase || detail.stock_po) && (
                <CollapsibleSection
                  title="Compra"
                  icon={<ShoppingCart className="w-3.5 h-3.5" />}
                  expanded={expandedSection === 'purchase'}
                  onToggle={() => toggleSection('purchase')}
                >
                  <div className="space-y-1.5 text-xs text-[var(--color-text-muted)]">
                    {detail.amazon_purchase && (
                      <>
                        <div>ASIN: <span className="text-[var(--color-text)] font-mono">{detail.amazon_purchase.asin}</span></div>
                        <div>Status: <span className="text-[var(--color-text)]">{detail.amazon_purchase.amazon_status}</span></div>
                        {detail.amazon_purchase.total_cost && can('global.view_money') && (
                          <div>Costo: <span className="text-[var(--color-text)]">${Number(detail.amazon_purchase.total_cost).toFixed(2)}</span></div>
                        )}
                        {detail.amazon_purchase.tracking_number && (
                          <div>Tracking: <span className="text-[var(--color-text)] font-mono">{detail.amazon_purchase.tracking_number}</span> ({detail.amazon_purchase.carrier})</div>
                        )}
                        {detail.amazon_purchase.amazon_order_id && (
                          <div>Amazon Order: <span className="text-[var(--color-text)] font-mono">{detail.amazon_purchase.amazon_order_id}</span></div>
                        )}
                      </>
                    )}
                    {detail.stock_po && (
                      <div>PO: <span className="text-[var(--color-text)]">{detail.stock_po.po_number}</span> · {detail.stock_po.status}/{detail.stock_po.fulfillment_status}</div>
                    )}
                    {detail.amazon_product && (
                      <div className="pt-1 border-t border-[var(--color-border)]">
                        <div className="truncate">{detail.amazon_product.title}</div>
                        {detail.amazon_product.brand && <div>Brand: {detail.amazon_product.brand}</div>}
                        {detail.amazon_product.price && can('global.view_money') && <div>AMZ Price: ${Number(detail.amazon_product.price).toFixed(2)}</div>}
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              )}

              {/* Buyer history */}
              {(detail.buyer_questions?.length > 0 || detail.buyer_orders?.length > 0) && (
                <CollapsibleSection
                  title="Historial buyer"
                  icon={<User className="w-3.5 h-3.5" />}
                  expanded={expandedSection === 'history'}
                  onToggle={() => toggleSection('history')}
                >
                  <div className="space-y-2 text-xs">
                    {detail.buyer_questions?.length > 0 && (
                      <div>
                        <div className="font-medium text-[var(--color-text)] mb-1">Preguntas previas ({detail.buyer_questions.length})</div>
                        {detail.buyer_questions.slice(0, 5).map((q: any) => (
                          <div key={q.question_id} className="mb-1 text-[var(--color-text-muted)]">
                            <div>Q: {q.text}</div>
                            {q.answer_text && <div className="ml-2 text-[var(--color-text)]">A: {q.answer_text}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    {detail.buyer_orders?.length > 0 && (
                      <div>
                        <div className="font-medium text-[var(--color-text)] mb-1">Otras ordenes ({detail.buyer_orders.length})</div>
                        {detail.buyer_orders.slice(0, 5).map((o: any) => (
                          <div key={o.ml_order_id} className="flex items-center gap-2 text-[var(--color-text-muted)]">
                            <span className="font-mono">#{o.ml_order_id}</span>
                            <span className={o.status === 'paid' ? 'text-green-500' : 'text-red-500'}>{o.status}</span>
                            <Can cap="global.view_money"><span>${Number(o.paid_amount).toFixed(2)}</span></Can>
                            <span className="truncate flex-1">{o.item_title}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              )}
            </div>

            {/* Message thread */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgs.length === 0 ? (
                <div className="text-center text-sm text-[var(--color-text-muted)] py-8">
                  Sin mensajes en esta conversación
                </div>
              ) : (
                [...msgs].reverse().map((msg: any, i: number) => {
                  const isBuyer = buyerId && msg.from.user_id === buyerId;
                  return (
                    <div key={i} className={`p-3 rounded-lg text-sm ${
                      isBuyer
                        ? 'bg-[var(--color-bg)] border border-[var(--color-border)] mr-12'
                        : 'bg-blue-500/10 border border-blue-500/20 ml-12'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        {isBuyer ? <User className="w-3 h-3 text-[var(--color-text-muted)]" /> : <Shield className="w-3 h-3 text-blue-500" />}
                        <span className="text-xs font-medium text-[var(--color-text-muted)]">
                          {isBuyer ? 'Buyer' : 'Seller'}
                        </span>
                        <span className="text-xs text-[var(--color-text-muted)]">
                          {new Date(msg.message_date?.created || msg.message_date?.received || '').toLocaleString()}
                        </span>
                        {!isBuyer && (
                          msg.message_date?.read
                            ? <span className="text-xs text-blue-500" title={`Leido: ${new Date(msg.message_date.read).toLocaleString()}`}>&#10003;&#10003;</span>
                            : <span className="text-xs text-[var(--color-text-muted)]">&#10003;</span>
                        )}
                      </div>
                      <div className="text-[var(--color-text)]">{msg.text}</div>
                      {msg.text_translated && (
                        <div className="text-xs text-[var(--color-text-muted)] mt-1 italic">{msg.text_translated}</div>
                      )}
                      {msg.message_attachments?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {msg.message_attachments.map((att: any, j: number) => {
                            const isImage = att.type?.startsWith('image/');
                            const proxyUrl = `/api/mercadolibre/messages/attachments/${encodeURIComponent(att.filename)}?seller_id=${sellerId}&site_id=${siteId}`;
                            return isImage ? (
                              <a key={j} href={proxyUrl} target="_blank" rel="noopener noreferrer"
                                 className="block max-w-[280px] rounded-lg overflow-hidden border border-[var(--color-border)] hover:border-[var(--color-primary)]">
                                <img src={proxyUrl} alt={att.original_filename} className="w-full h-auto max-h-[300px] object-contain bg-black/5" loading="lazy" />
                                <div className="flex items-center gap-2 px-2 py-1 bg-[var(--color-bg)] text-xs text-[var(--color-text-muted)]">
                                  <Download className="w-3 h-3" />
                                  <span className="truncate">{att.original_filename || att.filename}</span>
                                </div>
                              </a>
                            ) : (
                              <a key={j} href={proxyUrl} target="_blank" rel="noopener noreferrer"
                                 className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] text-xs">
                                <Download className="w-3.5 h-3.5 text-[var(--color-text-muted)]" />
                                <span className="text-[var(--color-text)]">{att.original_filename || att.filename}</span>
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            {conversationActive ? (
              <Can cap={sendCap}>
              <div className="px-4 py-3 border-t border-[var(--color-border)]">
                <div className="flex gap-2">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                    placeholder="Escribe un mensaje..."
                    className="flex-1 p-2.5 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-none focus:outline-none focus:border-[var(--color-primary)]"
                    rows={message.length > 120 || message.includes('\n') ? 5 : 2}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sending || !message.trim()}
                    className="self-end px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? '...' : 'Enviar'}
                  </button>
                </div>
                {templateMissing.length > 0 && message.includes('[COMPLETAR:') && (
                  <div className="mt-1.5 text-[11px] text-amber-600">
                    Falta completar a mano: {templateMissing.join(', ')}
                  </div>
                )}
                {/* Mandar la factura es de la pantalla de Mensajes: en el pop-up del depósito
                    (juego reducido de rutas) el botón no se ofrece. Los textos predeterminados sí
                    (2026-10-17): el depósito usa su propia ruta de datos de la venta. */}
                <div className="flex items-center justify-between gap-2 mt-2">
                  <MessageTemplates
                    resource={resource}
                    sellerId={sellerId}
                    onInsert={handleInsertTemplate}
                    canEdit={!soloLeerYResponder || can('orders.messages') || can('messages.send')}
                    compact={embedded}
                  />
                  {!soloLeerYResponder && <button
                    onClick={handleSendInvoice}
                    disabled={invoiceSending || invoiceStatus === 'sent'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                      invoiceStatus === 'sent'
                        ? 'bg-green-500/20 text-green-600 border border-green-500/30'
                        : 'bg-amber-500/10 text-amber-600 border border-amber-500/30 hover:bg-amber-500/20'
                    } disabled:opacity-50`}
                  >
                    {invoiceSending ? (
                      <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Enviando...</>
                    ) : invoiceStatus === 'sent' ? (
                      <><DollarSign className="w-3.5 h-3.5" /> Invoice Enviado</>
                    ) : (
                      <><DollarSign className="w-3.5 h-3.5" /> Send Invoice</>
                    )}
                  </button>}
                </div>
              </div>
              </Can>
            ) : conversationBlocked ? (
              <div className="px-4 py-3 border-t border-[var(--color-border)] text-xs text-[var(--color-text-muted)] bg-[var(--color-bg)]">
                Conversación bloqueada: {detail.messages?.conversation_status?.substatus || 'blocked'}
              </div>
            ) : (
              <Can cap={sendCap}>
              <div className="px-4 py-3 border-t border-[var(--color-border)]">
                <div className="flex gap-2">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                    placeholder="Escribe un mensaje..."
                    className="flex-1 p-2.5 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-none focus:outline-none focus:border-[var(--color-primary)]"
                    rows={message.length > 120 || message.includes('\n') ? 5 : 2}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sending || !message.trim()}
                    className="self-end px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? '...' : 'Enviar'}
                  </button>
                </div>
                {templateMissing.length > 0 && message.includes('[COMPLETAR:') && (
                  <div className="mt-1.5 text-[11px] text-amber-600">
                    Falta completar a mano: {templateMissing.join(', ')}
                  </div>
                )}
                {/* Mandar la factura es de la pantalla de Mensajes: en el pop-up del depósito
                    (juego reducido de rutas) el botón no se ofrece. Los textos predeterminados sí
                    (2026-10-17): el depósito usa su propia ruta de datos de la venta. */}
                <div className="flex items-center justify-between gap-2 mt-2">
                  <MessageTemplates
                    resource={resource}
                    sellerId={sellerId}
                    onInsert={handleInsertTemplate}
                    canEdit={!soloLeerYResponder || can('orders.messages') || can('messages.send')}
                    compact={embedded}
                  />
                  {!soloLeerYResponder && <button
                    onClick={handleSendInvoice}
                    disabled={invoiceSending || invoiceStatus === 'sent'}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                      invoiceStatus === 'sent'
                        ? 'bg-green-500/20 text-green-600 border border-green-500/30'
                        : 'bg-amber-500/10 text-amber-600 border border-amber-500/30 hover:bg-amber-500/20'
                    } disabled:opacity-50`}
                  >
                    {invoiceSending ? (
                      <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Enviando...</>
                    ) : invoiceStatus === 'sent' ? (
                      <><DollarSign className="w-3.5 h-3.5" /> Invoice Enviado</>
                    ) : (
                      <><DollarSign className="w-3.5 h-3.5" /> Send Invoice</>
                    )}
                  </button>}
                </div>
              </div>
              </Can>
            )}
          </>
        )}
      </div>
    </div>
  );
}
